import { AxiosError } from 'axios';
import { createAsyncAction } from 'typesafe-actions';

import {
  AggregatedJobsResponse,
  AggregatedPogMetricsResponse,
  AggregatedProductMetricsResponse,
  AggregatedPogSolverOutputResponse,
  AssortmentApiResponse,
  AuthResponse,
  DatasetInfo,
  Default,
  EditedJobResponse,
  FlipPogResponse,
  GetAssortmentOnlyResponse,
  GetAvailableAislesResponse,
  GetFetchCombinedPogResponse,
  GetFetchPogByStoreResponse,
  GetProductMetricsResponse,
  GetOptimizationStatusResponse,
  GetParametersResponse,
  GetDefaultProductMetricsResponse,
  GetDefaultPogMetricsResponse,
  GetUserPreferenceResponse,
  LaunchOptimizationJobResponse,
  OptimizationJobResponse,
  PDFDownloadStatus,
  CopySharedPogResponse,
  PushPogToStoreResponse,
  SaveDragAndDropEditsResponse,
  SaveMyNotesResponse,
  SaveNewEditedPogResponse,
  SaveUserPreferenceResponse,
  setResizedPogResponse,
  SetUserInfoResponse,
  SeedsResponse,
  PogSolverOutputResponse,
  MerchGuideLinkResponse,
  DeleteProtoPogPdfResult,
} from '../../types/responses';
import {
  AggregatedPogMetricsParams,
  AggregatedProductMetricsParams,
  AggregatedSolverOutputParams,
  PogSolverOutputParams,
  ProductsInfoParams,
  SaveUserPreferenceParams,
  SetUserRoleParams,
  SeedsParams,
  UpdateSeedSharedParams,
  ApproveSeedParams,
  RemovePogParams,
  DeleteProtoPogPdfParams,
  GetMerchGuideLinkParams,
  ChangeSharedPogNameParams,
  ChangePogNameParams,
  ChangePrivateAndSharedPogNamesParams,
  UpdateSeedOwnershipParams,
} from '../../types/requests';
import { SetResizedPogRequestParams } from '../viewAndEdit/viewAndEdit.types';
import { JobDeleteParams } from '../job/job.types';

export const apiActions = {
  getAssortmentParams: createAsyncAction(
    'api/GET_PARAMETERS_REQUEST',
    'api/GET_PARAMETERS_SUCCESS',
    'api/GET_PARAMETERS_FAILURE'
  )<void, GetParametersResponse, AxiosError>(),

  launchOptimizationJob: createAsyncAction(
    'api/LAUNCH_OPTIMIZATION_JOB_REQUEST',
    'api/LAUNCH_OPTIMIZATION_JOB_SUCCESS',
    'api/LAUNCH_OPTIMIZATION_JOB_FAILURE'
  )<void, LaunchOptimizationJobResponse, AxiosError>(),

  getOptimizationStatus: createAsyncAction(
    'api/GET_OPTIMIZATION_STATUS_REQUEST',
    'api/GET_OPTIMIZATION_STATUS_SUCCESS',
    'api/GET_OPTIMIZATION_STATUS_FAILURE'
  )<void, GetOptimizationStatusResponse, AxiosError>(),

  pushPogToStore: createAsyncAction(
    'api/PUSH_POG_TO_A_STORE_REQUEST',
    'api/PUSH_POG_TO_A_STORE_SUCCESS',
    'api/PUSH_POG_TO_A_STORE_FAILURE'
  )<void, PushPogToStoreResponse, AxiosError>(),

  copySharedPog: createAsyncAction(
    'api/COPY_SHARED_POG_REQUEST',
    'api/COPY_SHARED_POG_SUCCESS',
    'api/COPY_SHARED_POG_FAILURE'
  )<void, CopySharedPogResponse, AxiosError>(),

  getDatasetInfo: createAsyncAction(
    'api/GET_DATASET_INFO_REQUEST',
    'api/GET_DATASET_INFO_SUCCESS',
    'api/GET_DATASET_INFO_FAILURE'
  )<void, DatasetInfo, AxiosError>(),

  getAuth: createAsyncAction(
    'api/GET_AUTH_REQUEST',
    'api/GET_AUTH_SUCCESS',
    'api/GET_AUTH_FAILURE'
  )<void, AuthResponse, AxiosError>(),

  setUserRole: createAsyncAction(
    'api/POST_SET_USER_ROLE_REQUEST',
    'api/POST_SET_USER_ROLE_SUCCESS',
    'api/POST_SET_USER_ROLE_FAILURE'
  )<SetUserRoleParams, void, AxiosError>(),

  getAvailableAisles: createAsyncAction(
    'api/GET_AVAILABLE_AISLES_REQUEST',
    'api/GET_AVAILABLE_AISLES_SUCCESS',
    'api/GET_AVAILABLE_AISLES_FAILURE'
  )<void, GetAvailableAislesResponse, AxiosError>(),

  serverSideLogout: createAsyncAction(
    'api/SERVER_SIDE_LOGOUT_REQUEST',
    'api/SERVER_SIDE_LOGOUT_SUCCESS',
    'api/SERVER_SIDE_LOGOUT_FAILURE'
  )<void, void, AxiosError>(),

  setUserInfo: createAsyncAction(
    'user/SET_USER_INFO_REQUEST',
    'user/SET_USER_INFO_SUCCESS',
    'user/SET_USER_INFO_FAILURE'
  )<void, SetUserInfoResponse, AxiosError>(),

  getDefaultPogMetrics: createAsyncAction(
    'api/GET_DEFAULT_POG_METRICS_REQUEST',
    'api/GET_DEFAULT_POG_METRICS_SUCCESS',
    'api/GET_DEFAULT_POG_METRICS_FAILURE'
  )<void, GetDefaultPogMetricsResponse, AxiosError>(),

  getPogMetrics: createAsyncAction(
    'api/GET_POG_METRICS_REQUEST',
    'api/GET_POG_METRICS_SUCCESS',
    'api/GET_POG_METRICS_FAILURE'
  )<void, GetDefaultPogMetricsResponse, AxiosError>(),

  getDefaultProductMetrics: createAsyncAction(
    'api/GET_CURRENT_STATE_PRODUCTS_REQUEST',
    'api/GET_CURRENT_STATE_PRODUCTS_SUCCESS',
    'api/GET_CURRENT_STATE_PRODUCTS_FAILURE'
  )<void, GetDefaultProductMetricsResponse, AxiosError>(),

  getCurrentStateProductsAssortment: createAsyncAction(
    'api/GET_CURRENT_STATE_PRODUCTS_ASSORTMENT_REQUEST',
    'api/GET_CURRENT_STATE_PRODUCTS_ASSORTMENT_SUCCESS',
    'api/GET_CURRENT_STATE_PRODUCTS_ASSORTMENT_FAILURE'
  )<void, GetDefaultProductMetricsResponse, AxiosError>(),

  downloadOutputFiles: createAsyncAction(
    'api/GET_DOWNLOAD_OUTPUT_REQUEST',
    'api/GET_DOWNLOAD_OUTPUT_SUCCESS',
    'api/GET_DOWNLOAD_OUTPUT_FAILURE'
  )<void, null | PDFDownloadStatus, AxiosError>(),

  downloadPEGxFile: createAsyncAction(
    'api/GET_DOWNLOAD_PEGx_REQUEST',
    'api/GET_DOWNLOAD_PEGx_SUCCESS',
    'api/GET_DOWNLOAD_PEGx_FAILURE'
  )<void, void, AxiosError>(),

  downloadMyDataFile: createAsyncAction(
    'api/GET_DOWNLOAD_MY_DATA_FILE_REQUEST',
    'api/GET_DOWNLOAD_MY_DATA_FILE_SUCCESS',
    'api/GET_DOWNLOAD_MY_DATA_FILE_FAILURE'
  )<void, boolean, AxiosError>(),

  downloadAssortmentExcelFile: createAsyncAction(
    'api/DOWNLOAD_ASSORTMENT_EXCEL_FILE_REQUEST',
    'api/DOWNLOAD_ASSORTMENT_EXCEL_FILE_SUCCESS',
    'api/DOWNLOAD_ASSORTMENT_EXCEL_FILE_FAILURE'
  )<void, void, AxiosError>(),

  saveDragAndDropEdits: createAsyncAction(
    'api/SAVE_DRAG_AND_DROP_EDITS_REQUEST',
    'api/SAVE_DRAG_AND_DROP_EDITS_SUCCESS',
    'api/SAVE_DRAG_AND_DROP_EDITS_FAILURE'
  )<void, SaveDragAndDropEditsResponse, AxiosError>(),

  getOptimizationJobLimit: createAsyncAction(
    'api/GET_OPTIMIZATION_JOB_LIMIT_REQUEST',
    'api/GET_OPTIMIZATION_JOB_LIMIT_SUCCESS',
    'api/GET_OPTIMIZATION_JOB_LIMIT_FAILURE'
  )<void, OptimizationJobResponse, AxiosError>(),

  getPogSolverOutput: createAsyncAction(
    'api/GET_POG_SOLVER_OUTPUT_REQUEST',
    'api/GET_POG_SOLVER_OUTPUT_SUCCESS',
    'api/GET_POG_SOLVER_OUTPUT_FAILURE'
  )<PogSolverOutputParams, PogSolverOutputResponse, AxiosError>(),

  getProductMetrics: createAsyncAction(
    'api/GET_PRODUCT_METRICS_REQUEST',
    'api/GET_PRODUCT_METRICS_SUCCESS',
    'api/GET_PRODUCT_METRICS_FAILURE'
  )<void, GetProductMetricsResponse, AxiosError>(),

  getEditedJobs: createAsyncAction(
    'api/GET_EDITED_JOBS_REQUEST',
    'api/GET_EDITED_JOBS_SUCCESS',
    'api/GET_EDITED_JOBS_FAILURE'
  )<void, EditedJobResponse, AxiosError>(),

  saveNewEditedPog: createAsyncAction(
    'api/SAVE_NEW_EDITED_POG_REQUEST',
    'api/SAVE_NEW_EDITED_POG_SUCCESS',
    'api/SAVE_NEW_EDITED_POG_FAILURE'
  )<void, SaveNewEditedPogResponse, AxiosError>(),

  saveMyNotes: createAsyncAction(
    'api/SAVE_MY_NOTES_REQUEST',
    'api/SAVE_MY_NOTES_SUCCESS',
    'api/SAVE_MY_NOTES_FAILURE'
  )<void, SaveMyNotesResponse, AxiosError>(),

  updateEditedPog: createAsyncAction(
    'api/UPDATE_EDITED_POG_REQUEST',
    'api/UPDATE_EDITED_POG_SUCCESS',
    'api/UPDATE_EDITED_POG_FAILURE'
  )<void, SaveNewEditedPogResponse, AxiosError>(),

  autoSavePog: createAsyncAction(
    'api/AUTO_SAVE_POG_REQUEST',
    'api/AUTO_SAVE_POG_SUCCESS',
    'api/AUTO_SAVE_POG_FAILURE'
  )<void, SaveNewEditedPogResponse, AxiosError>(),

  autoSavedPogSolverOutput: createAsyncAction(
    'api/AUTO_SAVED_POG_SOLVER_OUTPUT_REQUEST',
    'api/AUTO_SAVED_POG_SOLVER_OUTPUT_SUCCESS',
    'api/AUTO_SAVED_POG_SOLVER_OUTPUT_FAILURE'
  )<string, PogSolverOutputResponse, AxiosError>(),

  default: createAsyncAction(
    'api/DEFAULT_REQUEST',
    'api/DEFAULT_SUCCESS',
    'api/DEFAULT_FAILURE'
  )<void, Default, AxiosError>(),

  getFetchPogByStore: createAsyncAction(
    'api/GET_FETCH_POG_BY_STORE_PARAM_REQUEST',
    'api/GET_FETCH_POG_BY_STORE_PARAM_SUCCESS',
    'api/GET_FETCH_POG_BY_STORE_PARAM_FAILURE'
  )<void, GetFetchPogByStoreResponse[], AxiosError>(),

  getFetchCombinedPog: createAsyncAction(
    'api/GET_FETCH_COMBINED_POG_REQUEST',
    'api/GET_FETCH_COMBINED_POG_SUCCESS',
    'api/GET_FETCH_COMBINED_POG_FAILURE'
  )<void, GetFetchCombinedPogResponse[], AxiosError>(),

  aggregatedJobs: createAsyncAction(
    'api/AGGREGATED_JOBS_REQUEST',
    'api/AGGREGATED_JOBS_SUCCESS',
    'api/AGGREGATED_JOBS_FAILURE'
  )<void, AggregatedJobsResponse, AxiosError>(),

  // Fetches specific jobs by id and merges them into the list instead of replacing it.
  // Used when a job_id from the URL is missing from the limited aggregatedJobs page.
  // The success payload keeps the requested ids, because the endpoint may answer with the
  // whole page rather than just the jobs that were asked for.
  aggregatedJobsById: createAsyncAction(
    'api/AGGREGATED_JOBS_BY_ID_REQUEST',
    'api/AGGREGATED_JOBS_BY_ID_SUCCESS',
    'api/AGGREGATED_JOBS_BY_ID_FAILURE'
  )<void, { jobIds: Array<string>; jobs: AggregatedJobsResponse }, AxiosError>(),

  aggregatedSolverOutput: createAsyncAction(
    'api/AGGREGATED_SOLVER_OUTPUT_REQUEST',
    'api/AGGREGATED_SOLVER_OUTPUT_SUCCESS',
    'api/AGGREGATED_SOLVER_OUTPUT_FAILURE'
  )<AggregatedSolverOutputParams, AggregatedPogSolverOutputResponse, AxiosError>(),

  aggregatedProductMetrics: createAsyncAction(
    'api/AGGREGATED_PRODUCT_METRICS_REQUEST',
    'api/AGGREGATED_PRODUCT_METRICS_SUCCESS',
    'api/AGGREGATED_PRODUCT_METRICS_FAILURE'
  )<AggregatedProductMetricsParams, AggregatedProductMetricsResponse, AxiosError>(),

  aggregatedPogMetrics: createAsyncAction(
    'api/AGGREGATED_POG_METRICS_REQUEST',
    'api/AGGREGATED_POG_METRICS_SUCCESS',
    'api/AGGREGATED_POG_METRICS_FAILURE'
  )<AggregatedPogMetricsParams, AggregatedPogMetricsResponse, AxiosError>(),

  getUserPreference: createAsyncAction(
    'api/GET_USER_PREFERENCE_REQUEST',
    'api/GET_USER_PREFERENCE_SUCCESS',
    'api/GET_USER_PREFERENCE_FAILURE'
  )<void, GetUserPreferenceResponse, AxiosError>(),

  saveUserPreference: createAsyncAction(
    'api/SAVE_USER_PREFERENCE_REQUEST',
    'api/SAVE_USER_PREFERENCE_SUCCESS',
    'api/SAVE_USER_PREFERENCE_FAILURE'
  )<SaveUserPreferenceParams, SaveUserPreferenceResponse, AxiosError>(),

  flipPog: createAsyncAction(
    'api/FLIP_POG_REQUEST',
    'api/FLIP_POG_SUCCESS',
    'api/FLIP_POG_FAILURE'
  )<void, FlipPogResponse, AxiosError>(),

  getAssortmentOnly: createAsyncAction(
    'api/GET_ASSORTMENT_ONLY_REQUEST',
    'api/GET_ASSORTMENT_ONLY_SUCCESS',
    'api/GET_ASSORTMENT_ONLY_FAILURE'
  )<void, GetAssortmentOnlyResponse, AxiosError>(),

  getAssortmentProductData: createAsyncAction(
    'api/GET_ASSORTMENT_PRODUCT_DATA_REQUEST',
    'api/GET_ASSORTMENT_PRODUCT_DATA_SUCCESS',
    'api/GET_ASSORTMENT_PRODUCT_DATA_FAILURE'
  )<ProductsInfoParams, AssortmentApiResponse, AxiosError>(),

  setResizedPog: createAsyncAction(
    'api/SET_RESIZED_POG_IN_EDITOR_REQUEST',
    'api/SET_RESIZED_POG_IN_EDITOR_SUCCESS',
    'api/SET_RESIZED_POG_IN_EDITOR_FAILURE'
  )<SetResizedPogRequestParams, setResizedPogResponse, AxiosError>(),

  deletePogJob: createAsyncAction(
    'api/DELETE_POG_JOB_REQUEST',
    'api/DELETE_POG_JOB_SUCCESS',
    'api/DELETE_POG_JOB_FAILURE'
  )<JobDeleteParams, JobDeleteParams, AxiosError>(),

  removePog: createAsyncAction(
    'api/REMOVE_POG_REQUEST',
    'api/REMOVE_POG_SUCCESS',
    'api/REMOVE_POG_FAILURE'
  )<RemovePogParams, RemovePogParams, AxiosError>(),

  deleteProtoPogPdf: createAsyncAction(
    'api/DELETE_PROTO_POG_PDF_REQUEST',
    'api/DELETE_PROTO_POG_PDF_SUCCESS',
    'api/DELETE_PROTO_POG_PDF_FAILURE'
  )<DeleteProtoPogPdfParams, DeleteProtoPogPdfResult, AxiosError>(),

  seeds: createAsyncAction(
    'api/SEEDS_REQUEST',
    'api/SEEDS_SUCCESS',
    'api/SEEDS_FAILURE'
  )<SeedsParams, SeedsResponse, AxiosError>(),

  updateSeedShared: createAsyncAction(
    'api/UPDATE_SEED_SHARED_REQUEST',
    'api/UPDATE_SEED_SHARED_SUCCESS',
    'api/UPDATE_SEED_SHARED_FAILURE'
  )<UpdateSeedSharedParams, UpdateSeedSharedParams, AxiosError>(),

  approveSeed: createAsyncAction(
    'api/APPROVE_SEED_REQUEST',
    'api/APPROVE_SEED_SUCCESS',
    'api/APPROVE_SEED_FAILURE'
  )<ApproveSeedParams, ApproveSeedParams, AxiosError>(),

  getMerchGuideLink: createAsyncAction(
    'api/GET_MERCH_GUIDE_LINK_REQUEST',
    'api/GET_MERCH_GUIDE_LINK_SUCCESS',
    'api/GET_MERCH_GUIDE_LINK_FAILURE'
  )<GetMerchGuideLinkParams, MerchGuideLinkResponse, AxiosError>(),

  changeSharedPogName: createAsyncAction(
    'api/CHANGE_SHARED_POG_NAME_REQUEST',
    'api/CHANGE_SHARED_POG_NAME_SUCCESS',
    'api/CHANGE_SHARED_POG_NAME_FAILURE'
  )<ChangeSharedPogNameParams, ChangeSharedPogNameParams, AxiosError>(),

  changePogName: createAsyncAction(
    'api/CHANGE_POG_NAME_REQUEST',
    'api/CHANGE_POG_NAME_SUCCESS',
    'api/CHANGE_POG_NAME_FAILURE'
  )<ChangePogNameParams, ChangePogNameParams, AxiosError>(),

  changePrivateAndSharedPogNames: createAsyncAction(
    'api/CHANGE_PRIVATE_AND_SHARED_POG_NAMES_REQUEST',
    'api/CHANGE_PRIVATE_AND_SHARED_POG_NAMES_SUCCESS',
    'api/CHANGE_PRIVATE_AND_SHARED_POG_NAMES_FAILURE'
  )<ChangePrivateAndSharedPogNamesParams, ChangePrivateAndSharedPogNamesParams, AxiosError>(),

  updateSeedOwnership: createAsyncAction(
    'api/UPDATE_SEED_OWNERSHIP_REQUEST',
    'api/UPDATE_SEED_OWNERSHIP_SUCCESS',
    'api/UPDATE_SEED_OWNERSHIP_FAILURE'
  )<UpdateSeedOwnershipParams, UpdateSeedOwnershipParams, AxiosError>(),
};
